import { deleteInformationApi, useInformationList } from "@/api/services/admin/information";
import { TGetInformationListDto } from "@/api/services/admin/information/dtos/get-information-list.dto";
import { useEnum } from "@/api/services/global/enum";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { Button } from "@/modules/irohana-components/elements/Button";
import { Table } from "@/modules/irohana-components/elements/Table";
import { DATE_FORMAT } from "@/constants/datetime";
import { EMPTY_STR } from "@/constants/string";
import { useDialog } from "@/hooks/useDialog";
import { useTable } from "@/modules/irohana-components/hooks/useTable";
import { useToast } from "@/hooks/useToast";
import { AppRoutes } from "@/routes/config";
import { dayjs } from "@/utils/dayjs";
import { showError } from "@/utils/error";
import { findLabelByValue } from "@/utils/object";
import { getInformationStatus } from "@/features/information/utils/informationStatus";
import { getDynamicRoute } from "@/utils/url";
import { useQueryClient } from "@tanstack/react-query";
import { MRT_ColumnDef } from "material-react-table";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Stack } from "@mui/material";
import { styled } from "@mui/system";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { PaginationNew } from "@/modules/irohana-components/elements/PaginationNew";

export const InformationTable = () => {
  const { data: informationTypes } = useEnum("EInformationType");
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirmDelete = useDialog();

  const [heightTable] = useState<string>("calc(100vh - 350px)");

  const handleDelete = async (id: number) => {
    try {
      const agreeToDelete = await confirmDelete.show();
      if (!agreeToDelete) return;
      const rs = await deleteInformationApi(id);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.INFORMATION.FETCH_LIST] });
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
    } catch (error) {
      showError(error);
    }
  };

  const columns = useMemo<MRT_ColumnDef<TGetInformationListDto>[]>(
    () => [
      {
        header: "作成日時",
        size: 60,
        Cell: ({ row }) => {
          const { createdAt } = row.original;
          return createdAt ? dayjs(createdAt).format(DATE_FORMAT) : EMPTY_STR.DATE;
        },
      },
      {
        header: "ステータス",
        size: 60,
        Cell: ({ row }) => getInformationStatus(row.original),
      },
      {
        header: "タイトル",
        Cell: ({ row }) => {
          return row.original.title || EMPTY_STR.TEXT;
        },
      },
      {
        header: "タグ",
        size: 120,
        Cell: ({ row }) => {
          return row.original.type ? findLabelByValue(informationTypes, row.original.type) : EMPTY_STR.TEXT;
        },
      },
      {
        header: "配信開始日",
        size: 120,
        Cell: ({ row }) => {
          const { startDate } = row.original;
          return startDate ? dayjs(startDate).format(DATE_FORMAT) : EMPTY_STR.DATE;
        },
      },
      {
        header: "配信終了日",
        size: 120,
        Cell: ({ row }) => {
          const { endDate } = row.original;
          return endDate ? dayjs(endDate).format(DATE_FORMAT) : EMPTY_STR.DATE;
        },
      },
      {
        header: "削除",
        id: "delete",
        Cell: ({ row }) => {
          const { id } = row.original;
          return (
            <Button size="sm" variant="outline" onClick={() => handleDelete(id)}>
              削除
            </Button>
          );
        },
        grow: false,
        size: 100,
      },
      {
        header: "修正・更新",
        id: "edit",
        Cell: ({ row }) => {
          const { id } = row.original;
          return (
            <Button size="sm" component={Link} to={getDynamicRoute(AppRoutes.informationEdit, { id })}>
              修正
            </Button>
          );
        },
        grow: false,
        size: 100,
      },
    ],
    [informationTypes],
  );

  const { data, isLoading } = useInformationList();
  const { table } = useTable({
    columns,
    rows: data?.data ?? [],
    pinningColumns: ["delete", "edit"],
    renderEmptyRowsFallback: ({}) => <span></span>,
  });

  return (
    <>
      <Table table={table} isLoading={isLoading} height={heightTable} minHeight={150} />
      <PaginationStyle>
        <PaginationNew totalPage={data?.pagination.totalPage} />
      </PaginationStyle>
      <ConfirmDeleteModal
        open={confirmDelete.open}
        onClose={confirmDelete.cancel}
        onOk={confirmDelete.confirm}
        message="お知らせを削除してもよろしいですか？"
      />
    </>
  );
};

const PaginationStyle = styled(Stack)`
  width: 100%;
  padding-top: 10px;
  margin-top: 16px;
  gap: 8px;
`;
